import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from 'tss-react/mui';
import {useIntl} from 'react-intl';
import GestureIcon from '@mui/icons-material/Gesture';

const useStyles = makeStyles()((theme) => ({
  field: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    font: 'inherit',
    gap: 6,
    justifyContent: 'center',
    minHeight: 120,
    padding: 16,
    textAlign: 'center',
    width: '100%',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  filled: {
    borderColor: theme.palette.primary.main,
    borderStyle: 'solid',
  },
  disabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
    '&:hover': {
      borderColor: theme.palette.divider,
    },
  },
  icon: {
    color: theme.palette.primary.main,
    fontSize: 32,
  },
  prompt: {
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
  },
  hint: {
    color: theme.palette.text.secondary,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(13),
  },
  preview: {
    maxHeight: 90,
    maxWidth: '100%',
  },
  change: {
    color: theme.palette.primary.main,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 700,
  },
}));

// A button rather than a drawing surface: scrolling the document must never leave a stray stroke behind.
const SignatureField = ({value, disabled, onOpen}) => {
  const {classes, cx} = useStyles();
  const intl = useIntl();

  return (
    <button
      type='button'
      disabled={disabled}
      className={cx(classes.field, value && classes.filled, disabled && classes.disabled)}
      onClick={onOpen}
    >
      {value ? (
        <>
          <img src={value} alt={intl.formatMessage({id: 'sign.pad.preview'})} className={classes.preview} />
          <span className={classes.change}>{intl.formatMessage({id: 'sign.pad.change'})}</span>
        </>
      ) : (
        <>
          <GestureIcon className={classes.icon} />
          <span className={classes.prompt}>{intl.formatMessage({id: 'sign.pad.openButton'})}</span>
          <span className={classes.hint}>{intl.formatMessage({id: 'sign.pad.openHint'})}</span>
        </>
      )}
    </button>
  );
};

SignatureField.propTypes = {
  disabled: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  value: PropTypes.string,
};

export default SignatureField;
